import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18next from 'i18next'
import { documentGenerationService } from '@/services/api/document-generation'
import { queryKeys } from '@/config/queryKeys'
import type {
  GenerateDocumentData,
  UpdateDocumentStatusData,
  ListGeneratedDocumentsParams,
} from '@/services/api/document-generation'

export const useGeneratedDocuments = (patientId: string) => {
  return useQuery({
    queryKey: queryKeys.generatedDocuments.byPatient(patientId),
    queryFn: () => documentGenerationService.listGeneratedDocuments({ patientId, limit: 100 }),
    enabled: !!patientId,
  })
}

export const useAllGeneratedDocuments = (
  params?: Omit<ListGeneratedDocumentsParams, 'patientId'>
) => {
  return useQuery({
    queryKey: queryKeys.generatedDocuments.list(params as Record<string, unknown>),
    queryFn: () => documentGenerationService.listGeneratedDocuments(params ?? {}),
  })
}

export const useInfiniteAllGeneratedDocuments = (
  params?: Omit<ListGeneratedDocumentsParams, 'patientId' | 'page'>
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.generatedDocuments.list(params as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      documentGenerationService.listGeneratedDocuments({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.limit)
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined
    },
  })
}

export const useReadiness = (patientId: string) => {
  return useQuery({
    queryKey: queryKeys.generatedDocuments.readiness(patientId),
    queryFn: () => documentGenerationService.checkReadiness(patientId),
    enabled: false,
  })
}

export const useGenerateDocument = (patientId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: GenerateDocumentData) =>
      documentGenerationService.generateDocument(patientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.byPatient(patientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.lists() })
      toast.success(i18next.t('documents:generateSuccess'))
    },
    onError: () => {
      toast.error(i18next.t('documents:generateFailed'))
    },
  })
}

export const useGenerateBundle = (patientId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => documentGenerationService.generateBundle(patientId),
    onSuccess: (docs) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.byPatient(patientId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.lists() })
      toast.success(i18next.t('documents:bundleSuccess', { count: docs.length }))
    },
    onError: () => {
      toast.error(i18next.t('documents:generateFailed'))
    },
  })
}

export const useUpdateDocumentStatus = (patientId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, data }: { documentId: string; data: UpdateDocumentStatusData }) =>
      documentGenerationService.updateStatus(documentId, data),
    onSuccess: () => {
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.generatedDocuments.byPatient(patientId),
        })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.lists() })
      toast.success(i18next.t('documents:statusUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('documents:generateFailed'))
    },
  })
}

export const useRegenerateDocument = (patientId?: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) => documentGenerationService.regenerate(documentId),
    onSuccess: () => {
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.generatedDocuments.byPatient(patientId),
        })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.lists() })
      toast.success(i18next.t('documents:generateSuccess'))
    },
    onError: () => {
      toast.error(i18next.t('documents:generateFailed'))
    },
  })
}

export const useDeleteGeneratedDocument = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) =>
      documentGenerationService.deleteGeneratedDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.lists() })
      toast.success(i18next.t('documents:documentDeleted'))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useViewGeneratedDocument = () => {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const { signedUrl } = await documentGenerationService.getDownloadUrl(documentId)
      return signedUrl
    },
    onSuccess: (signedUrl) => {
      window.open(signedUrl, '_blank')
    },
    onError: () => {
      toast.error(i18next.t('documents:generateFailed'))
    },
  })
}

export const useDownloadDocument = () => {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const { signedUrl, fileName } = await documentGenerationService.getDownloadUrl(documentId)
      const response = await fetch(signedUrl)
      const blob = await response.blob()
      return { blob, fileName }
    },
    onSuccess: ({ blob, fileName }) => {
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    },
    onError: () => {
      toast.error(i18next.t('documents:generateFailed'))
    },
  })
}
